import { IApiResponse } from 'src/app/shared/interfaces/common-api.interface';

export interface IMasterPlaceholderResponse extends IApiResponse {
  data?: IPlaceholder[];
}

export interface IPlaceholder {
  active: boolean;
  placeholderKey: string;
  placeholderValue: string;
}

export interface IPublishedFormResponse extends IApiResponse {
  data?: IPublishedForm[];
}

export interface IPublishedForm {
  formCode: string;
  formName: string;
  name?: string;
}

export interface IUploadedFile {
  name: string;
  fileIndex: number;
}

export interface IUploadEmailResponse extends IApiResponse {
  data?: IUploadedFileURL;
}

export interface IWhatsAppMediaResponse extends IApiResponse {
  data?: IUploadedFileURL;
}

export interface IUploadedFileURL {
  url: string[];
}

export enum ChannelType {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
  RCS = 'RCS',
  PUSH_NOTIFICATION = 'PUSH_NOTIFICATION',
}

export enum ChannelName {
  EMAIL = 'Email',
  SMS = 'SMS',
  WHATSAPP = 'WhatsApp',
  RCS = 'RCS',
  PUSH_NOTIFICATION = 'Push Notification',
}

export const ChannelKeys: Record<ChannelName, keyof typeof ChannelName> = {
  [ChannelName.EMAIL]: 'EMAIL',
  [ChannelName.SMS]: 'SMS',
  [ChannelName.WHATSAPP]: 'WHATSAPP',
  [ChannelName.RCS]: 'RCS',
  [ChannelName.PUSH_NOTIFICATION]: 'PUSH_NOTIFICATION',
};

export enum TemplateMode {
  EDIT = 'Edit',
  VIEW = 'View',
  CREATE = 'New',
  COPY = 'Clone',
}

export enum FileType {
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  DOCUMENT = 'DOCUMENT',
}

export enum MediaDocType {
  STATIC = 'Static',
  DYNAMIC = 'Dynamic',
}

export enum ButtonActionType {
  QUICK_REPLY = 'QUICK_REPLY',
  CALL_BACK = 'CALL_BACK',
  STATIC_CALLBACK = 'STATIC_CALLBACK',
  DYNAMIC_CALLBACK = 'DYNAMIC_CALLBACK',
  STATIC_CTA = 'STATIC_CTA',
  WEB_FORM_URL = 'WEB_FORM_URL',
  DYNAMIC_CTA = 'DYNAMIC_CTA',
  LOCATION = 'LOCATION',
  CALENDAR = 'CALENDAR',
}

export enum ButtonActionValue {
  QUICK_REPLY = 'Quick Reply',
  STATIC_CALLBACK = 'Static Callback',
  DYNAMIC_CALLBACK = 'Dynamic Callback',
  STATIC_CTA = 'Static CTA',
  DYNAMIC_CTA = 'Dynamic CTA',
  WEB_FORM_URL = 'Web Form Url',
  LOCATION = 'Location',
  CALENDAR = 'Calendar',
}

export interface ITemplateCreateResponse extends IApiResponse {
  data?: ITemplateSuccessResponse;
}

export interface ITemplateSuccessResponse {
  templateCode: string;
  message: string;
  category?: string;
  previousCategory?: string;
  templateName: string;
}

export interface ITemplatePayload {
  code?: string;
  name: string;
  channel: string;
  category?: string;
  language: string;
  status: string;
  organizationDetail: {
    organization: string;
    vertical: string;
    division: string;
    department: string;
  };
  whatsAppTemplate?: IWhatsAppPayload;
  smsTemplate?: ISMSPayload;
}

export interface ISMSPayload {
  dltId: string;
  senderId: string;
  principalId: string;
  content: string;
  formCode?: string;
  urlShorten: boolean;
}

export interface IWhatsAppPayload {
  metaId: string;
  template: [
    {
      type: string;
      sequence: string;
      content: string;
      formCode?: string;
      media?: ITemplateMedia;
      button?: IWhatsAppCTAButton[];
    },
  ];
}

export interface ITemplateMedia {
  type: string;
  url: string;
  fileName: string;
}

export interface IWhatsAppCTAButton {
  subType: string;
  sequence: string;
  label: string;
  url: string;
  action: string;
}

export interface IMediaOption {
  label: string;
  value: string;
}

export enum MediaOptionType {
  LINK = 'link',
  ID = 'id',
}

export type CONTENT_TYPE = 'IMAGE' | 'BUTTON' | 'TEXT';

export interface ImageContent {
  url: string;
  mediaUrl?: string;
  height: number;
  width: number;
}

export interface ButtonContent {
  text: string;
  actionUrl?: string;
  style: string;
  size: string;
  alignment: string;
  backgroundColor?: string;
  textColor?: string;
  type: string;
  selectedWebForm?: string;
  imageUrl?: string;
  paramPlaceholder?: string;
  urlShorten?: boolean;
}

export interface TextContent {
  htmlText: string;
  attachedFormCode?: string;
  attachedFormName?: string;
  urlShorten?: boolean;
}

export interface Content {
  type: CONTENT_TYPE;
  properties: ImageContent | ButtonContent | TextContent;
}
export interface Column {
  screenPercentage: string;
  content: Content;
}

export interface Row {
  columns: Column[];
}

export interface Layout {
  rows: Row[];
  footer?: string;
  maxRows: number;
}

export interface MasterJson {
  templateBackgroundColor: string;
  attachment?: string;
  layout: Layout;
  mode: 'TEMPLATE' | 'CUSTOM';
  formCode?: string;
  formName?: string;
}

export interface SelectedPosition {
  rowIndex: number | null;
  columnIndex: number | null;
}

export interface TemplateLayout {
  code: string;
  name: string;
  templateJson: string;
  templateImageUrl: string;
  description: string;
}

export interface ItemplateLayoutResponse extends IApiResponse {
  data?: TemplateLayout[];
}

export interface BodyJson {
  attachments: string[];
  subject: string;
  footer?: string;
}

export interface ItemplateContentResponse extends IApiResponse {
  data: TemplateContent;
}

export interface TemplateContent {
  subject: string;
  templateCode: string;
  htmlContent: string;
  templateJson: string;
  attachments?: string[];
  formcode?: string;
  tinyButtonJson?: string;
}

export interface IRCSAgentsResponse extends IApiResponse {
  data: IRCSAgent[];
}

export interface IRCSAgent {
  number: string;
  name: string;
  code: string;
  id: string;
  botId: string;
}

export enum TemplateType {
  TEXT_MESSAGE = 'textMessage',
  PDF_TEXT_MESSAGE = 'pdfTextMessage',
  CAROUSEL_TEXT_MESSAGE = 'carouselTextMessage',
  STANDALONE_RICH_CARD = 'standaloneRichCard',
}

export enum ContentType {
  TITLE = 'TITLE',
  DESCRIPTION = 'DESCRIPTION',
}

export enum Orientation {
  HORIZONTAL = 'HORIZONTAL',
  VERTICAL = 'VERTICAL',
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

export enum RCSLayout {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export interface Card {
  id: number;
  key?: string;
  value?: string;
  dimensions: string;
  activeImage?: string;
  inactiveImage?: string;
  isSelected?: boolean;
  aspectRatio: number;
  displayAspectRatio?: string;
  uploader?: string;
  minSizeKB?: number;
  maxSizeKB?: number;
  height?: string;
  width?: string;
  thumbnailConfig?: any;
}

export interface IDeliveryLogsResponse extends IApiResponse {
  data?: IDeliveryLogsSuccessResponse[];
}

export interface IDeliveryLogsSuccessResponse {
  recipientId: string;
  status: string;
  errorDesc: string | null;
  tinyUrls: ITinyUrls[];
}

export interface ITinyUrls {
  shortUrl: string;
  longUrl: string;
  placeholderName: string;
  clicks: number;
}

export interface ILogsPayload {
  channel: string;
  templateCode: string;
  shortenerProvider: string;
  type: string;
}

export interface IemailButtonConfig {
  actionUrl: string;
  paramPlaceholder: string;
  staticUrl?: boolean;
  formCode?: string;
  type: string;
  rowIndex: number;
  columnIndex: number;
}
